import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

function formatCount(value) {
  if (value == null || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return number.toLocaleString("ru-RU", { maximumFractionDigits: 0 });
}

function formatPct(value) {
  if (value == null || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  return `${number.toLocaleString("ru-RU", {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  })}%`;
}

function formatSigned(value) {
  if (value == null || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  const formatted = Math.abs(number).toLocaleString("ru-RU", {
    maximumFractionDigits: 0,
  });
  if (number > 0) return `+${formatted}`;
  if (number < 0) return `−${formatted}`;
  return formatted;
}

function formatDay(value) {
  if (!value) return "";
  const [, month, day] = String(value).slice(0, 10).split("-");
  return day && month ? `${day}.${month}` : value;
}

function eachIsoDay(from, to) {
  const days = [];
  if (!from || !to || from > to) return days;
  const current = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  while (current <= end) {
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, "0");
    const day = String(current.getDate()).padStart(2, "0");
    days.push(`${year}-${month}-${day}`);
    current.setDate(current.getDate() + 1);
  }
  return days;
}

function shortLabel(value) {
  const text = String(value || "");
  return text.length > 32 ? `${text.slice(0, 30)}…` : text;
}

function MetricCard({ label, value, hint, tone }) {
  return (
    <article className={`dash-metric${tone ? ` is-${tone}` : ""}`}>
      <p className="dash-metric-label">{label}</p>
      <p className="dash-metric-value">{value}</p>
      {hint ? <p className="dash-metric-hint">{hint}</p> : null}
    </article>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="dash-chart-tooltip">
      <p className="dash-chart-tooltip-date">{label}</p>
      {payload.map((item) => (
        <p key={item.dataKey}>
          {item.name}: <strong>{formatCount(item.value)}</strong>
        </p>
      ))}
    </div>
  );
}

const LegalDashboard = ({ summary }) => {
  const totals = summary.totals || {};
  const groups = summary.groups || [];
  const groupResolved = summary.group_resolved || groups;
  const series = summary.series || [];
  const remaining = Number(totals.remaining);
  const resolvedPct = Number(totals.resolved_pct);
  const byDate = Object.fromEntries(series.map((item) => [item.date, item]));
  const daily = eachIsoDay(summary.date_from, summary.date_to).map((date) => {
    const row = byDate[date];
    return {
      date,
      label: formatDay(date),
      incoming: row ? Number(row.incoming) || 0 : null,
      resolved: row ? Number(row.resolved) || 0 : null,
    };
  });
  const groupChart = groupResolved.map((item) => ({
    name: item.name,
    incoming: Number(item.incoming) || 0,
    resolved: Number(item.resolved) || 0,
  }));
  const groupChartHeight = Math.max(280, 72 + groupChart.length * 52);

  return (
    <div className="dash-legal">
      <section className="dash-metrics">
        <MetricCard
          label="Поступило"
          value={formatCount(totals.incoming)}
          hint="сумма за период"
        />
        <MetricCard
          label="Решено"
          value={formatCount(totals.resolved)}
          hint="сумма за период"
        />
        <MetricCard
          label="Остаток"
          value={formatSigned(totals.remaining)}
          hint="поступило − решено"
          tone={remaining < 0 ? "good" : remaining > 0 ? "warn" : ""}
        />
        <MetricCard
          label="% решено"
          value={formatPct(totals.resolved_pct)}
          hint="решено / поступило за период"
          tone={resolvedPct >= 100 ? "good" : ""}
        />
      </section>

      <div className="dash-split dash-legal-charts">
        <section className="dash-panel dash-side-chart">
          <div className="dash-panel-head">
            <h2>По группам</h2>
          </div>
          <div
            className="dash-side-chart-wrap"
            style={{ minHeight: groupChartHeight }}
          >
            {groupChart.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={groupChart}
                  layout="vertical"
                  margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e5e7eb"
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: "#6b7280" }}
                    allowDecimals={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={168}
                    tick={{ fontSize: 11, fill: "#4b5563" }}
                    tickFormatter={shortLabel}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend />
                  <Bar
                    dataKey="incoming"
                    name="Поступило"
                    fill="#8aa0b8"
                    radius={[0, 4, 4, 0]}
                    maxBarSize={16}
                  />
                  <Bar
                    dataKey="resolved"
                    name="Решено"
                    fill="#6f8f7a"
                    radius={[0, 4, 4, 0]}
                    maxBarSize={16}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="reports-hint">Нет групп за период</p>
            )}
          </div>
        </section>

        <section className="dash-panel dash-side-chart">
          <div className="dash-panel-head">
            <h2>По дням</h2>
          </div>
          <div className="dash-side-chart-wrap">
            {daily.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={daily}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e5e7eb"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: "#6b7280" }}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#6b7280" }}
                    allowDecimals={false}
                    width={40}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend />
                  <Bar
                    dataKey="incoming"
                    name="Поступило"
                    fill="#8aa0b8"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={28}
                  />
                  <Bar
                    dataKey="resolved"
                    name="Решено"
                    fill="#6f8f7a"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={28}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : null}
          </div>
        </section>
      </div>

      <section className="dash-panel">
        <div className="dash-panel-head">
          <h2>Строки отчёта</h2>
        </div>
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>Вид</th>
                <th className="is-num">Поступило</th>
                <th className="is-num">Решено</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <React.Fragment key={group.name}>
                  <tr className="dash-table-group">
                    <th colSpan={3}>{group.name}</th>
                  </tr>
                  {(group.items || []).map((item) => (
                    <tr key={`${group.name}-${item.name}`}>
                      <td>{item.name}</td>
                      <td className="is-num">{formatCount(item.incoming)}</td>
                      <td className="is-num">{formatCount(item.resolved)}</td>
                    </tr>
                  ))}
                  <tr className="dash-table-subtotal">
                    <td>Итого</td>
                    <td className="is-num">{formatCount(group.incoming)}</td>
                    <td className="is-num">{formatCount(group.resolved)}</td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Всего за период</td>
                <td className="is-num">{formatCount(totals.incoming)}</td>
                <td className="is-num">{formatCount(totals.resolved)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <section className="dash-legend">
        <h2>Как считаем</h2>
        <p>
          Берём загрузки за выбранные даты
          {summary.loaded_dates?.length
            ? ` (есть отчёты за ${summary.loaded_dates
                .map(formatDay)
                .join(", ")})`
            : ""}
          . Пустые ячейки «Поступило» и «Решено» считаем как 0. Если у строки
          нет названия группы, берём последнюю группу выше по файлу.
        </p>
        <dl>
          <div>
            <dt>Сумма за период</dt>
            <dd>
              Поступило и решено складываем по всем дням периода, в том числе
              по группам и видам обращений. График слева — итог группы,
              справа — сумма каждого дня.
            </dd>
          </div>
          <div>
            <dt>Остаток и % решено</dt>
            <dd>
              Остаток = поступило − решено за те же даты; может быть меньше
              нуля, если закрыли хвост прошлых дней. Процент = решено ÷
              поступило; если поступило 0, процент не считаем.
            </dd>
          </div>
        </dl>
      </section>
    </div>
  );
};

export default LegalDashboard;
